import React, { useState, useEffect, useMemo } from 'react';
import { 
  Building,
  ChevronDown,
  ChevronRight,
  Check,
  XCircle,
  AlertCircle
} from 'lucide-react';
import styles from '../CSS/CreateRoleForm.module.css';

const OUPermissionSelector = ({ 
  organizationalUnits = [],
  selectedOUs = [],
  onChange 
}) => {
  const [expandedOUs, setExpandedOUs] = useState(new Set());
  
  const getInitialMode = useMemo(() => {
    if (selectedOUs.length === 0) return 'none';
    
    const countAllOUs = (ous) => {
      let count = 0;
      ous.forEach(ou => {
        count++;
        if (ou.children && ou.children.length > 0) {
          count += countAllOUs(ou.children);
        }
      });
      return count;
    };
    
    const totalOUs = countAllOUs(organizationalUnits);
    if (selectedOUs.length === totalOUs && totalOUs > 0) return 'all';
    
    return 'specific';
  }, [selectedOUs.length, organizationalUnits.length]);
  
  const [selectionMode, setSelectionMode] = useState(getInitialMode);

  useEffect(() => {
    setSelectionMode(prevMode => {
      if (prevMode !== getInitialMode) {
        return getInitialMode;
      }
      return prevMode;
    });
  }, [getInitialMode]);

  const getAllOUPaths = (ous) => {
    let paths = [];
    ous.forEach(ou => {
      paths.push(ou.orgUnitPath);
      if (ou.children && ou.children.length > 0) {
        paths = [...paths, ...getAllOUPaths(ou.children)];
      }
    });
    return paths;
  };

  const handleModeChange = (mode) => {
    setSelectionMode(mode);
    if (mode === 'all') {
      const allOUPaths = getAllOUPaths(organizationalUnits);
      onChange(allOUPaths);
    } else if (mode === 'none') {
      onChange([]);
    }
  };

  const toggleExpanded = (ouPath) => {
    const newExpanded = new Set(expandedOUs);
    if (newExpanded.has(ouPath)) {
      newExpanded.delete(ouPath);
    } else {
      newExpanded.add(ouPath);
    }
    setExpandedOUs(newExpanded);
  };

  const isOUSelected = (ouPath) => {
    return selectedOUs.includes(ouPath);
  };

  const toggleOUSelection = (ouPath) => {
    if (selectionMode !== 'specific') {
      setSelectionMode('specific');
    }

    const newSelected = isOUSelected(ouPath)
      ? selectedOUs.filter(path => path !== ouPath)
      : [...selectedOUs, ouPath];
    
    onChange(newSelected);
  };

  const renderOUTree = (ous, level = 0) => {
    return ous.map(ou => {
      const isExpanded = expandedOUs.has(ou.orgUnitPath);
      const isSelected = isOUSelected(ou.orgUnitPath);
      const hasChildren = ou.children && ou.children.length > 0;

      return (
        <div key={ou.orgUnitPath} className={styles.ouTreeItem}>
          <div 
            className={styles.ouItemRow}
            style={{ marginLeft: `${level * 20}px` }}
          >
            {hasChildren ? (
              <button
                onClick={() => toggleExpanded(ou.orgUnitPath)}
                className={styles.ouExpandButton}
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <span className={styles.ouExpandSpacer}></span>
            )}
            
            <button
              onClick={() => toggleOUSelection(ou.orgUnitPath)}
              className={`${styles.ouSelectButton} ${isSelected ? styles.ouSelected : ''}`}
            >
              <div className={styles.ouCheckbox}>
                {isSelected && <Check size={14} />}
              </div>
              
              <Building size={16} className={styles.ouItemIcon} />
              
              <div className={styles.ouItemInfo}>
                <span className={styles.ouItemName}>{ou.name}</span>
                <span className={styles.ouItemPath}>{ou.orgUnitPath}</span>
              </div>
              
              {ou.userCount > 0 && (
                <span className={styles.ouUserCount}>{ou.userCount}</span>
              )}
            </button>
          </div>

          {isExpanded && hasChildren && (
            <div className={styles.ouChildren}>
              {renderOUTree(ou.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={styles.ouSelectorContainer}>
      <div className={styles.ouSelectorHeader}>
        <h4 className={styles.ouSelectorTitle}>
          <Building size={18} />
          Unidades Organizativas Permitidas
        </h4>
        <p className={styles.ouSelectorDescription}>
          Selecciona las unidades organizativas a las que este rol tendrá acceso en la gestión de correos
        </p>
      </div>

      <div className={styles.ouSelectionModes}>
        <button
          onClick={() => handleModeChange('all')}
          className={`${styles.ouModeButton} ${selectionMode === 'all' ? styles.ouModeActive : ''}`}
        >
          <Check size={14} />
          Todas las OUs
        </button>
        
        <button
          onClick={() => handleModeChange('specific')}
          className={`${styles.ouModeButton} ${selectionMode === 'specific' ? styles.ouModeActive : ''}`}
        >
          <Building size={14} />
          Específicas
        </button>
        
        <button
          onClick={() => handleModeChange('none')}
          className={`${styles.ouModeButton} ${selectionMode === 'none' ? styles.ouModeActive : ''}`}
        >
          <XCircle size={14} />
          Ninguna
        </button>
      </div>

      {selectionMode === 'specific' && (
        <>
          <div className={styles.ouSelectedInfo}>
            <AlertCircle size={14} />
            <span>{selectedOUs.length} unidades seleccionadas</span>
          </div>

          <div className={styles.ouTreeContainer}>
            {organizationalUnits.length > 0 ? (
              renderOUTree(organizationalUnits)
            ) : (
              <div className={styles.ouEmptyState}>
                <Building size={48} />
                <p>No hay unidades organizativas disponibles</p>
              </div>
            )}
          </div>
        </>
      )}

      {selectionMode === 'all' && (
        <div className={styles.ouInfoBox}>
          <Check size={16} />
          <span>Este rol tendrá acceso a todas las unidades organizativas</span>
        </div>
      )}

      {selectionMode === 'none' && (
        <div className={styles.ouWarningBox}>
          <AlertCircle size={16} />
          <span>Este rol no tendrá acceso a ninguna unidad organizativa</span>
        </div>
      )}
    </div>
  );
};

export default OUPermissionSelector;